import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/pages/PublicLayout";
import LandingPage from "@/pages/LandingPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ChauffeurLogin from "@/pages/ChauffeurLogin";
import ChauffeurDashboard from "@/pages/ChauffeurDashboard";
import Blog from "@/pages/Blog";
import BookingOptions from "@/pages/BookingOptions";
import TripPlannerPage from "@/pages/TripPlannerPage";
import JeddahChauffeurServiceBenefits from "@/pages/JeddahChauffeurServiceBenefits";
import JeddahAirportTransferGuide from "@/pages/JeddahAirportTransferGuide";
import UberVsPrivateChauffeurSaudiArabia from "@/pages/UberVsPrivateChauffeurSaudiArabia";
import RiyadhBusinessTravelChauffeurService from "@/pages/RiyadhBusinessTravelChauffeurService";
import JeddahToMakkahPrivateTransferBenefits from "@/pages/JeddahToMakkahPrivateTransferBenefits";
import HowToBookChauffeurServiceSaudiArabia from "@/pages/HowToBookChauffeurServiceSaudiArabia";
import BestWayToTravelFromJeddahAirportToMakkah from "@/pages/BestWayToTravelFromJeddahAirportToMakkah";
import PrivateTransferVsTaxiUmrahPilgrims from "@/pages/PrivateTransferVsTaxiUmrahPilgrims";
import JeddahToMakkahPrivateCarGuide from "@/pages/JeddahToMakkahPrivateCarGuide";
import MakkahToMadinahTransferFamilies from "@/pages/MakkahToMadinahTransferFamilies";
import PremiumChauffeurBusinessTravellersRiyadh from "@/pages/PremiumChauffeurBusinessTravellersRiyadh";
import AirportTransferJeddahWhatTravellersShouldKnow from "@/pages/AirportTransferJeddahWhatTravellersShouldKnow";
import SafeTransportOptionsUmrahPilgrims from "@/pages/SafeTransportOptionsUmrahPilgrims";
import WhyGCCFamiliesPreferPrivateChauffeurServices from "@/pages/WhyGCCFamiliesPreferPrivateChauffeurServices";
import LuxuryCarWithDriverSaudiArabiaGuide from "@/pages/LuxuryCarWithDriverSaudiArabiaGuide";
import UaeToSaudiUmrahTransport from "@/pages/UaeToSaudiUmrahTransport";
import QatarToSaudiUmrahTransport from "@/pages/QatarToSaudiUmrahTransport";
import KuwaitToSaudiUmrahTransport from "@/pages/KuwaitToSaudiUmrahTransport";
import BahrainToSaudiUmrahTransport from "@/pages/BahrainToSaudiUmrahTransport";
import OmanToSaudiUmrahTransport from "@/pages/OmanToSaudiUmrahTransport";
import TravellingFromUAEForUmrahPrivateTransport from "@/pages/TravellingFromUAEForUmrahPrivateTransport";
import BestChauffeurServiceGCCFamiliesMakkah from "@/pages/BestChauffeurServiceGCCFamiliesMakkah";
import PrivateUmrahTransportFamiliesGCC from "@/pages/PrivateUmrahTransportFamiliesGCC";
import JeddahChauffeurService from "@/pages/JeddahChauffeurService";
import UberVsChauffeurSaudiArabia from "@/pages/UberVsChauffeurSaudiArabia";
import ChauffeurServiceSaudiArabia from "@/pages/ChauffeurServiceSaudiArabia";
import ChauffeurServiceJeddah from "@/pages/ChauffeurServiceJeddah";
import ChauffeurServiceRiyadh from "@/pages/ChauffeurServiceRiyadh";
import ChauffeurServiceDammam from "@/pages/ChauffeurServiceDammam";
import AirportTransferJeddah from "@/pages/AirportTransferJeddah";
import JeddahAirportToMakkahTransfer from "@/pages/JeddahAirportToMakkahTransfer";
import MakkahToMadinahTransfer from "@/pages/MakkahToMadinahTransfer";
import UmrahChauffeurService from "@/pages/UmrahChauffeurService";
import CorporateChauffeurServiceSaudiArabia from "@/pages/CorporateChauffeurServiceSaudiArabia";
import PremiumFamilyTransportSaudiArabia from "@/pages/PremiumFamilyTransportSaudiArabia";
import BMW7SeriesChauffeurServiceJeddah from "@/pages/BMW7SeriesChauffeurServiceJeddah";
import PremiumChauffeurHajjUmrah from "@/pages/PremiumChauffeurHajjUmrah";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import BlackRideExperience from "@/pages/BlackRideExperience";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import GeneratedLandingPage from "@/pages/GeneratedLandingPage";
import GeneratedBlogArticle from "@/pages/GeneratedBlogArticle";
import { generatedLandingPaths, generatedBlogPaths } from "@/data/seoContent.mjs";

function App() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    html.setAttribute("data-theme", "dark");
    body.setAttribute("data-theme", "dark");
    html.style.colorScheme = "dark";
    body.style.backgroundColor = "#050505";
    body.style.color = "#f5efe4";
    body.classList.remove("light");
    body.classList.add("dark");
  }, []);

  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AnalyticsTracker />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/jeddah-chauffeur-service-benefits" element={<JeddahChauffeurServiceBenefits />} />
                <Route path="/blog/jeddah-airport-transfer-guide" element={<JeddahAirportTransferGuide />} />
                <Route path="/blog/uber-vs-private-chauffeur-saudi-arabia" element={<UberVsPrivateChauffeurSaudiArabia />} />
                <Route path="/blog/riyadh-business-travel-chauffeur-service" element={<RiyadhBusinessTravelChauffeurService />} />
                <Route path="/blog/jeddah-to-makkah-private-transfer-benefits" element={<JeddahToMakkahPrivateTransferBenefits />} />
                <Route path="/blog/how-to-book-a-chauffeur-service-in-saudi-arabia" element={<HowToBookChauffeurServiceSaudiArabia />} />
                <Route path="/blog/best-way-to-travel-from-jeddah-airport-to-makkah" element={<BestWayToTravelFromJeddahAirportToMakkah />} />
                <Route path="/blog/private-transfer-vs-taxi-for-umrah-pilgrims" element={<PrivateTransferVsTaxiUmrahPilgrims />} />
                <Route path="/blog/jeddah-to-makkah-private-car-complete-guide" element={<JeddahToMakkahPrivateCarGuide />} />
                <Route path="/blog/makkah-to-madinah-transfer-best-options-for-families" element={<MakkahToMadinahTransferFamilies />} />
                <Route path="/blog/premium-chauffeur-service-for-business-travellers-in-riyadh" element={<PremiumChauffeurBusinessTravellersRiyadh />} />
                <Route path="/blog/airport-transfer-in-jeddah-what-travellers-should-know" element={<AirportTransferJeddahWhatTravellersShouldKnow />} />
                <Route path="/blog/safe-transport-options-for-umrah-pilgrims" element={<SafeTransportOptionsUmrahPilgrims />} />
                <Route path="/blog/why-gcc-families-prefer-private-chauffeur-services-in-saudi-arabia" element={<WhyGCCFamiliesPreferPrivateChauffeurServices />} />
                <Route path="/blog/luxury-car-with-driver-in-saudi-arabia-complete-booking-guide" element={<LuxuryCarWithDriverSaudiArabiaGuide />} />
                <Route path="/blog/uae-to-saudi-umrah-transport" element={<UaeToSaudiUmrahTransport />} />
                <Route path="/blog/qatar-to-saudi-umrah-transport" element={<QatarToSaudiUmrahTransport />} />
                <Route path="/blog/kuwait-to-saudi-umrah-transport" element={<KuwaitToSaudiUmrahTransport />} />
                <Route path="/blog/bahrain-to-saudi-umrah-transport" element={<BahrainToSaudiUmrahTransport />} />
                <Route path="/blog/oman-to-saudi-umrah-transport" element={<OmanToSaudiUmrahTransport />} />
                <Route path="/blog/travelling-from-uae-for-umrah-book-private-transport-in-saudi-arabia" element={<TravellingFromUAEForUmrahPrivateTransport />} />
                <Route path="/blog/best-chauffeur-service-for-gcc-families-visiting-makkah" element={<BestChauffeurServiceGCCFamiliesMakkah />} />
                <Route path="/blog/private-umrah-transport-for-families-from-dubai-abu-dhabi-doha-kuwait" element={<PrivateUmrahTransportFamiliesGCC />} />
                {generatedBlogPaths.map((path) => (
                  <Route key={path} path={path} element={<GeneratedBlogArticle />} />
                ))}
                <Route path="/jeddah-chauffeur-service" element={<JeddahChauffeurService />} />
                <Route path="/uber-vs-chauffeur-service-saudi-arabia" element={<UberVsChauffeurSaudiArabia />} />
                <Route path="/chauffeur-service-saudi-arabia" element={<ChauffeurServiceSaudiArabia />} />
                <Route path="/chauffeur-service-jeddah" element={<ChauffeurServiceJeddah />} />
                <Route path="/chauffeur-service-riyadh" element={<ChauffeurServiceRiyadh />} />
                <Route path="/chauffeur-service-dammam" element={<ChauffeurServiceDammam />} />
                <Route path="/airport-transfer-jeddah" element={<AirportTransferJeddah />} />
                <Route path="/jeddah-airport-transfer" element={<AirportTransferJeddah />} />
                <Route path="/jeddah-airport-to-makkah-transfer" element={<JeddahAirportToMakkahTransfer />} />
                <Route path="/makkah-to-madinah-transfer" element={<MakkahToMadinahTransfer />} />
                <Route path="/umrah-chauffeur-service" element={<UmrahChauffeurService />} />
                <Route path="/corporate-chauffeur-service-saudi-arabia" element={<CorporateChauffeurServiceSaudiArabia />} />
                <Route path="/premium-family-transport-saudi-arabia" element={<PremiumFamilyTransportSaudiArabia />} />
                <Route path="/bmw-7-series-chauffeur-service-jeddah" element={<BMW7SeriesChauffeurServiceJeddah />} />
                <Route path="/premium-chauffeur-service-hajj-umrah" element={<PremiumChauffeurHajjUmrah />} />
                <Route path="/experience" element={<BlackRideExperience />} />
                <Route path="/blackride-experience" element={<BlackRideExperience />} />
                {generatedLandingPaths.map((path) => (
                  <Route key={path} path={path} element={<GeneratedLandingPage />} />
                ))}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsConditions />} />
              </Route>

              <Route path="/trip-planner" element={<TripPlannerPage />} />
              <Route path="/booking/options" element={<BookingOptions />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute roles={["admin", "dispatcher"]} fallback="/admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/chauffeur" element={<ChauffeurLogin />} />
              <Route
                path="/chauffeur/dashboard"
                element={
                  <ProtectedRoute roles={["driver"]} fallback="/chauffeur">
                    <ChauffeurDashboard />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
